import { DOCUMENT, DestroyRef, Injectable, InjectionToken, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, catchError, exhaustMap, from, fromEvent, merge, timer } from 'rxjs';
import * as Sentry from '@sentry/angular';

const REFRESH_GUARD_KEY = 'deployment-refresh';
const REFRESH_COOLDOWN_MS = 60_000;
const CHECK_INTERVAL_MS = 60 * 60 * 1000;
const TELEMETRY_TIMEOUT_MS = 1000;
const DEPLOYMENT_ID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

export const DEPLOYMENT_REFRESH_SENTRY = new InjectionToken<
  Pick<typeof Sentry, 'isEnabled' | 'captureMessage' | 'flush'>
>('Deployment refresh Sentry', { providedIn: 'root', factory: () => Sentry });

@Injectable({ providedIn: 'root' })
export class DeploymentUpdateService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly sentry = inject(DEPLOYMENT_REFRESH_SENTRY);
  private readonly controller = new AbortController();
  private started = false;
  private reloading = false;
  private checkFailed = false;

  start(): void {
    const currentId = this.document.querySelector<HTMLMetaElement>(
      'meta[name="deployment-id"]',
    )?.content;
    if (this.started || !currentId || !DEPLOYMENT_ID.test(currentId)) {
      return;
    }
    this.started = true;
    this.destroyRef.onDestroy(() => this.controller.abort());
    this.zone.runOutsideAngular(() => {
      merge(
        timer(0, CHECK_INTERVAL_MS),
        fromEvent(this.document, 'visibilitychange'),
        fromEvent(globalThis, 'online'),
      )
        .pipe(
          exhaustMap(() => {
            if (this.document.hidden || globalThis.navigator.onLine === false || this.reloading) {
              return EMPTY;
            }
            return from(this.checkDeployment(currentId)).pipe(
              catchError((error: unknown) => {
                if (!this.controller.signal.aborted && !this.checkFailed) {
                  console.warn('Unable to check for a new deployment; will retry.', error);
                  this.checkFailed = true;
                }
                return EMPTY;
              }),
            );
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    });
  }

  private async checkDeployment(currentId: string): Promise<void> {
    const response = await globalThis.fetch(new URL('index.html', this.document.baseURI), {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(15_000)]),
    });
    if (!response.ok) {
      throw new Error(`Deployment check failed with HTTP ${response.status}`);
    }
    const html = await response.text();
    const latest = new DOMParser().parseFromString(html, 'text/html');
    const nextId = latest.querySelector<HTMLMetaElement>('meta[name="deployment-id"]')?.content;
    if (!nextId || !DEPLOYMENT_ID.test(nextId)) {
      throw new Error('Deployment check returned an invalid deployment id');
    }
    this.checkFailed = false;
    if (nextId !== currentId && !this.controller.signal.aborted) {
      await this.refresh(currentId, nextId);
    }
  }

  private async refresh(currentId: string, nextId: string): Promise<void> {
    const guard = `${currentId}:${nextId}`;
    const now = Date.now();
    // A stale deep-link response may need another reload after this cooldown.
    try {
      const stored = globalThis.sessionStorage.getItem(REFRESH_GUARD_KEY)?.split(':');
      const retryAt = Number(stored?.[2]);
      if (
        stored?.length === 3 &&
        `${stored[0]}:${stored[1]}` === guard &&
        Number.isFinite(retryAt) &&
        retryAt > now &&
        retryAt <= now + REFRESH_COOLDOWN_MS
      ) {
        return;
      }
      globalThis.sessionStorage.setItem(REFRESH_GUARD_KEY, `${guard}:${now + REFRESH_COOLDOWN_MS}`);
    } catch (error) {
      console.error(
        'Cannot safely refresh for a new deployment: session storage is unavailable.',
        error,
      );
      return;
    }
    this.reloading = true;
    await this.reportRefresh(currentId, nextId);
    if (!this.controller.signal.aborted) {
      globalThis.location.reload();
    }
  }

  private async reportRefresh(currentId: string, nextId: string): Promise<void> {
    try {
      if (!this.sentry.isEnabled() || this.controller.signal.aborted) {
        return;
      }
      this.sentry.captureMessage('Forced refresh for new deployment', {
        level: 'info',
        tags: { app_name: 'web-wel-admin', event_type: 'deployment_refresh' },
        contexts: { deployment: { current_id: currentId, next_id: nextId } },
      });
      await this.flushRefreshEvent();
    } catch (error) {
      if (!this.controller.signal.aborted) {
        console.warn('Unable to report deployment refresh; reloading anyway.', error);
      }
    }
  }

  private async flushRefreshEvent(): Promise<void> {
    const flushed = await this.sentry.flush(TELEMETRY_TIMEOUT_MS);
    if (!flushed && !this.controller.signal.aborted) {
      console.warn('Deployment refresh telemetry did not flush in time; reloading anyway.');
    }
  }
}
